import { useState, useRef, useEffect, useCallback } from 'react';
import { AlertTriangle, Shield, AlertCircle, Loader2, RotateCcw, CheckCircle2 } from 'lucide-react';
import { getDocumentText, applyRedlineWithTrackChanges, isWordApiAvailable, scrollToText } from '../utils/wordApi';
import {
  getEditStatuses,
  subscribeDocumentChanges,
  markDocumentChanged,
  getDocumentChangedAt,
} from '../utils/changeStatus';
import { resolveContractContext } from '../utils/contractResolver';
import { analyzeContractRisks, type RiskItem } from '../utils/openai';

interface CurrentContract {
  contractId: string;
  tenantId: string;
  templateId: string;
  templateTypeId: string;
  templateTypeName: string;
}

// Simple hash function to detect document changes
function hashString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash.toString();
}

export function RiskAnalysis() {
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [risks, setRisks] = useState<RiskItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [replacingRiskId, setReplacingRiskId] = useState<number | null>(null);
  const [allRisksResolved, setAllRisksResolved] = useState<boolean>(false);
  const [analysisProgress, setAnalysisProgress] = useState<{ current: number; total: number } | null>(null);
  const [currentContract, setCurrentContract] = useState<CurrentContract | null>(null);
  // Risks the user has applied (as tracked changes). Kept so we can reconcile
  // them against the document — if the user later REJECTS the tracked change in
  // Word, the risk is re-surfaced back into the actionable list.
  const [appliedRisks, setAppliedRisks] = useState<RiskItem[]>([]);
  // Set when the document changes (an apply here, or a redline applied in the
  // Redlining pane) so the last analysis may be out of date.
  const [stale, setStale] = useState<boolean>(false);

  // Track document state and replaced risks
  const documentHashRef = useRef<string | null>(null);
  const replacedRisksRef = useRef<Set<string>>(new Set());
  // riskKey -> timestamp of the last apply. A risk fix applied within the last
  // few seconds is never re-surfaced by reconciliation: a fresh tracked change
  // interleaves struck-old + inserted-new text in body.text and can briefly
  // read as its own original. The grace window lets it settle.
  const recentlyAppliedRiskRef = useRef<Map<string, number>>(new Map());
  const APPLY_GRACE_MS = 8000;

  const ctxKeyRef = useRef<string | null>(null);

  const persistAppliedRisks = useCallback((items: RiskItem[]) => {
    setAppliedRisks(items);
    try {
      const ctxKey = ctxKeyRef.current;
      if (ctxKey) {
        sessionStorage.setItem(`ezylex:appliedRisks:${ctxKey}`, JSON.stringify(items));
      }
    } catch {}
  }, []);

  // Load current contract context (works with both WOPI and SharePoint/Auth modes)
  useEffect(() => {
    resolveContractContext().then((ctx) => {
      if (!ctx) return;
      setCurrentContract(ctx as CurrentContract);
      // Hydrate from the cross-tab cache so the user sees any prior analysis
      // immediately instead of the empty "Click to analyze" state.
      try {
        const ctxKey = ctx.contractId || ctx.tenantId;
        ctxKeyRef.current = ctxKey || null;
        if (ctxKey) {
          const raw = sessionStorage.getItem(`ezylex:risks:${ctxKey}`);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed?.risks) && parsed.risks.length > 0) {
              setRisks(parsed.risks as RiskItem[]);
            }
            // If the document was changed (e.g. a redline applied on the
            // Redlining pane) AFTER this analysis was cached, it's stale — panes
            // mount one at a time, so we detect it here on mount rather than via
            // a live event that fired while this pane was unmounted. We show the
            // "document changed — re-run" banner (a cheap, clear prompt) rather
            // than silently kicking off a slow AI re-run that looks stuck.
            const analysisTs = typeof parsed?.ts === 'number' ? parsed.ts : 0;
            if (getDocumentChangedAt() > analysisTs) {
              setStale(true);
            }
          }
          const appliedRaw = sessionStorage.getItem(`ezylex:appliedRisks:${ctxKey}`);
          if (appliedRaw) {
            const parsedApplied = JSON.parse(appliedRaw);
            if (Array.isArray(parsedApplied)) {
              setAppliedRisks(parsedApplied as RiskItem[]);
              // Rebuild the "already fixed" set so a re-run doesn't re-list risks
              // whose fix is still standing (the set is in-memory, lost on mount).
              (parsedApplied as RiskItem[]).forEach((r) =>
                replacedRisksRef.current.add(`${r.riskySentence}-${r.severity}`)
              );
            }
          }
        }
      } catch {}
    }).catch((err) => console.error('Error loading current contract:', err));
  }, []);

  // Listen for risk updates that originated elsewhere (e.g., Home re-run).
  useEffect(() => {
    const onUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (Array.isArray(detail?.risks)) setRisks(detail.risks as RiskItem[]);
    };
    window.addEventListener('ezylex:risks-updated', onUpdate as EventListener);
    return () => window.removeEventListener('ezylex:risks-updated', onUpdate as EventListener);
  }, []);

  // Listen for global "analyze-all" event (fired by Home's Re-run analysis button)
  useEffect(() => {
    const handler = () => {
      if (!isAnalyzing && currentContract) {
        handleAnalyze();
      }
    };
    window.addEventListener('ezylex:analyze-all', handler);
    return () => window.removeEventListener('ezylex:analyze-all', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentContract, isAnalyzing]);

  const riskKey = (r: RiskItem) => `${r.riskySentence}-${r.severity}`;

  const publishRisks = useCallback((updated: RiskItem[]) => {
    try {
      const ctxKey = ctxKeyRef.current;
      if (ctxKey) {
        sessionStorage.setItem(
          `ezylex:risks:${ctxKey}`,
          JSON.stringify({ risks: updated, ts: Date.now() })
        );
      }
      window.dispatchEvent(
        new CustomEvent('ezylex:risks-updated', { detail: { risks: updated } })
      );
    } catch {}
  }, []);

  // Reconcile applied risk fixes against the live document. If the user
  // REJECTED a tracked change in Word's Review pane, its original text is back
  // in the body — we re-surface that risk so it can be applied again.
  const reconcileAppliedRisks = useCallback(async () => {
    if (appliedRisks.length === 0) return;
    let statuses;
    try {
      statuses = await getEditStatuses(
        appliedRisks.map((r) => ({ anchor: r.riskySentence, rewritten: r.revisedClause }))
      );
    } catch {
      return;
    }
    const now = Date.now();
    for (const [k, ts] of recentlyAppliedRiskRef.current) {
      if (now - ts > APPLY_GRACE_MS) recentlyAppliedRiskRef.current.delete(k);
    }
    const stillApplied: RiskItem[] = [];
    const reSurfaced: RiskItem[] = [];
    statuses.forEach((s, i) => {
      const applied = appliedRisks[i];
      const ts = recentlyAppliedRiskRef.current.get(riskKey(applied));
      const inGrace = ts && now - ts <= APPLY_GRACE_MS;
      if (s.status === 'rejected' && !inGrace) reSurfaced.push(applied);
      else stillApplied.push(applied);
    });
    if (reSurfaced.length === 0) return;

    persistAppliedRisks(stillApplied);
    // Let a later re-analysis surface these again, and drop the "all resolved".
    reSurfaced.forEach((r) => replacedRisksRef.current.delete(riskKey(r)));
    setAllRisksResolved(false);
    setStale(true);
    setRisks((prev) => {
      const have = new Set(prev.map(riskKey));
      const merged = [...prev, ...reSurfaced.filter((r) => !have.has(riskKey(r)))];
      publishRisks(merged);
      return merged;
    });
    // A rejected fix reverted the document — record it for the other panes too.
    markDocumentChanged();
  }, [appliedRisks, persistAppliedRisks, publishRisks]);

  // Keep the latest reconcile in a ref so the window/Office listeners below can
  // be registered once yet always see fresh applied-risk state.
  const reconcileRef = useRef<() => void>(() => {});
  reconcileRef.current = () => { void reconcileAppliedRisks(); };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const debounced = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => reconcileRef.current(), 400);
    };
    const onFocus = () => reconcileRef.current();
    const onVisible = () => {
      if (document.visibilityState === 'visible') reconcileRef.current();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    const unsub = subscribeDocumentChanges(debounced);
    // Reconcile shortly after mount too (covers a reject that happened while the
    // pane was closed / the add-in was reloaded).
    const initial = setTimeout(() => reconcileRef.current(), 1500);
    // Poll as a guaranteed fallback: Word Online does not reliably fire
    // selection/paragraph events when the user clicks Reject/Accept in the
    // Review ribbon, so the event triggers above can miss it. The reconcile
    // itself is cheap (no-ops with no applied risks; one Word.run otherwise).
    const poll = setInterval(() => reconcileRef.current(), 3000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      unsub();
      if (timer) clearTimeout(timer);
      clearTimeout(initial);
      clearInterval(poll);
    };
  }, []);

  // The document changed elsewhere (e.g. a redline applied in the Redlining
  // pane) — the last analysis may be out of date.
  useEffect(() => {
    const onDocChanged = () => setStale(true);
    window.addEventListener('ezylex:document-changed', onDocChanged as EventListener);
    return () => window.removeEventListener('ezylex:document-changed', onDocChanged as EventListener);
  }, []);

  const handleAnalyze = async (): Promise<void> => {
    if (!isWordApiAvailable()) {
      setError('Word API is not available. Please ensure you are running this add-in in Microsoft Word.');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setAllRisksResolved(false);
    setStale(false);

    try {
      // Get document text
      const documentText = await getDocumentText();
      
      if (!documentText || documentText.trim().length === 0) {
        setError('Document appears to be empty. Please add some content to analyze.');
        setIsAnalyzing(false);
        return;
      }

      // Calculate document hash to detect changes
      const currentHash = hashString(documentText);
      
      // If document hasn't changed and we've already analyzed, check if all risks are resolved
      if (documentHashRef.current === currentHash && risks.length === 0 && replacedRisksRef.current.size > 0) {
        setAllRisksResolved(true);
        setIsAnalyzing(false);
        return;
      }

      // If document changed, reset replaced risks tracking
      if (documentHashRef.current !== currentHash) {
        replacedRisksRef.current.clear();
        documentHashRef.current = currentHash;
      }

      // Analyze risks using OpenAI (with chunking for long documents)
      console.log('Starting risk analysis, document length:', documentText.length);
      const tenantId = currentContract?.tenantId || '';
      if (!tenantId) {
        setError('Contract context not available. Please reload the add-in.');
        setIsAnalyzing(false);
        return;
      }
      const result = await analyzeContractRisks(documentText, tenantId, (current, total) => {
        setAnalysisProgress({ current, total });
      });
      
      // Clear progress
      setAnalysisProgress(null);
      
      console.log('Total risks found:', result.risks.length);
      console.log('Sample risks:', result.risks.slice(0, 2));
      
      // Filter out risks that have already been replaced
      const newRisks = result.risks.filter((risk) => {
        const riskKey = `${risk.riskySentence}-${risk.severity}`;
        return !replacedRisksRef.current.has(riskKey);
      });

      console.log('New risks after filtering:', newRisks.length);
      setRisks(newRisks);

      // Share results with the Home gauge so the score blends playbook gaps
      // with AI-identified risks. Cache in sessionStorage so a later Home
      // visit (or tab switch back) keeps the blended score visible.
      try {
        const ctxKey = currentContract?.contractId || currentContract?.tenantId;
        if (ctxKey) {
          sessionStorage.setItem(
            `ezylex:risks:${ctxKey}`,
            JSON.stringify({ risks: newRisks, ts: Date.now() })
          );
        }
        window.dispatchEvent(
          new CustomEvent('ezylex:risks-updated', { detail: { risks: newRisks } })
        );
      } catch {}

      // If no new risks found and document hasn't changed, show all good message
      if (newRisks.length === 0 && replacedRisksRef.current.size > 0) {
        setAllRisksResolved(true);
      } else if (newRisks.length === 0 && result.risks.length === 0) {
        // No risks found at all - show a message
        setError('No risks were identified in this document. This could mean the document is well-written, or the analysis needs adjustment.');
      }
    } catch (err) {
      console.error('Error analyzing risks:', err);
      setError(err instanceof Error ? err.message : 'Failed to analyze contract risks. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleReplace = async (risk: RiskItem, index: number): Promise<void> => {
    if (!isWordApiAvailable()) {
      setError('Word API is not available. Please ensure you are running this add-in in Microsoft Word.');
      return;
    }

    setReplacingRiskId(index);
    setError(null);

    try {
      // Apply as a TRACKED change (like redlines) so the user can accept or
      // reject it in Word's Review pane. If it is later rejected,
      // reconcileAppliedRisks re-surfaces this risk into the list.
      const res = await applyRedlineWithTrackChanges(risk.riskySentence, risk.revisedClause);
      if (!res.success) {
        setError(
          res.error
            ? `Could not apply the fix: ${res.error}`
            : 'Could not locate the risky text in the document to apply the fix.'
        );
        return;
      }

      // Remember it as applied so we can reconcile against a later accept/reject.
      persistAppliedRisks([...appliedRisks, risk]);
      // Keep re-analysis from re-listing it while the fix stands.
      replacedRisksRef.current.add(riskKey(risk));
      // Grace window: don't let an immediate reconcile re-surface it.
      recentlyAppliedRiskRef.current.set(riskKey(risk), Date.now());

      // Remove from the actionable list + refresh the Home gauge.
      setRisks((prevRisks) => {
        const updated = prevRisks.filter((_, i) => i !== index);
        publishRisks(updated);
        if (updated.length === 0) setAllRisksResolved(true);
        return updated;
      });

      // The document changed — flag the analysis as possibly stale and let the
      // other panes / Home gauge know (persisted; panes mount one at a time).
      setStale(true);
      markDocumentChanged();
    } catch (err) {
      console.error('Error applying risk fix:', err);
      setError(err instanceof Error ? err.message : 'Failed to apply the fix. Please try again.');
    } finally {
      setReplacingRiskId(null);
    }
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'medium':
        return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'low':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getSeverityLabelColor = (severity: string): string => {
    switch (severity) {
      case 'high':
        return 'bg-red-600';
      case 'medium':
        return 'bg-orange-500';
      case 'low':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const riskCounts = {
    high: risks.filter((r) => r.severity === 'high').length,
    medium: risks.filter((r) => r.severity === 'medium').length,
    low: risks.filter((r) => r.severity === 'low').length,
  };

  return (
    <div className="p-4 h-full bg-gradient-to-br from-red-50 to-white overflow-y-auto" style={{ fontSize: '13px' }}>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 bg-red-600 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-base font-bold text-gray-900">Risk Analysis</h1>
            <p className="text-xs text-gray-600">Identify and mitigate contract risks</p>
          </div>
          {appliedRisks.length > 0 && (
            <button
              type="button"
              onClick={() => reconcileRef.current()}
              title="Refresh — check for risk fixes you accepted or rejected in Word"
              className="ml-auto inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Refresh
            </button>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <button
            onClick={handleAnalyze}
            disabled={isAnalyzing}
            className={`w-full px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${
              isAnalyzing
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
            style={{ fontSize: '13px' }}
          >
            {isAnalyzing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Analyzing Risks...</span>
              </>
            ) : risks.length > 0 ? (
              <>
                <RotateCcw className="w-4 h-4" />
                <span>Re-run risk analysis</span>
              </>
            ) : (
              <span>Run risk analysis</span>
            )}
          </button>
          {analysisProgress && analysisProgress.total > 1 && (
            <div className="mt-2">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-red-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(analysisProgress.current / analysisProgress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
          {risks.length > 0 && !isAnalyzing && (
            <p className="text-[11px] text-gray-500 mt-2 text-center">
              Showing the latest analysis for this contract.
            </p>
          )}
        </div>

        {stale && !isAnalyzing && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <p className="text-amber-800" style={{ fontSize: '12px' }}>
                The document changed since the last analysis. Re-run risk
                analysis to see up-to-date results.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <p className="text-red-800" style={{ fontSize: '12px' }}>{error}</p>
            </div>
          </div>
        )}

        {allRisksResolved && risks.length === 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center mb-4">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-green-900 mb-2">Everything Looks Good!</h3>
            <p className="text-green-700" style={{ fontSize: '12px' }}>
              All identified risks have been addressed. Your contract appears to be in good shape.
            </p>
            <p className="text-green-600 mt-2" style={{ fontSize: '11px' }}>
              If you make changes to the document, click "View Risk Analysis" again to check for new risks.
            </p>
          </div>
        )}

        {risks.length > 0 && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-white rounded-lg shadow-sm border border-red-200 p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600">High Risk</span>
                  <AlertCircle className="w-4 h-4 text-red-600" />
                </div>
                <p className="text-xl font-bold text-gray-900">{riskCounts.high}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-orange-200 p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600">Medium Risk</span>
                  <AlertTriangle className="w-4 h-4 text-orange-600" />
                </div>
                <p className="text-xl font-bold text-gray-900">{riskCounts.medium}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-yellow-200 p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600">Low Risk</span>
                  <Shield className="w-4 h-4 text-yellow-600" />
            </div>
                <p className="text-xl font-bold text-gray-900">{riskCounts.low}</p>
          </div>
        </div>

            <div className="space-y-3">
              {risks.map((risk, index) => (
            <div
              key={index}
                  className={`bg-white rounded-lg shadow-sm border-2 p-3 ${getSeverityColor(risk.severity)}`}
            >
                  <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                          className={`px-2 py-1 rounded text-xs font-semibold text-white ${getSeverityLabelColor(risk.severity)}`}
                        >
                          {risk.severity.toUpperCase()} RISK
                        </span>
                        <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs">
                          {risk.category}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleReplace(risk, index)}
                      disabled={replacingRiskId === index}
                      className={`px-3 py-1 rounded transition-colors flex items-center gap-1 ${
                        replacingRiskId === index
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                      style={{ fontSize: '12px' }}
                      title="Replace risky text with revised clause"
                    >
                      {replacingRiskId === index ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <>
                          <RotateCcw className="w-3 h-3" />
                          <span>R</span>
                        </>
                      )}
                    </button>
                  </div>

                  <div className="space-y-2">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1" style={{ fontSize: '12px' }}>
                        Risky Sentence:
                      </h4>
                      <button
                        onClick={() => scrollToText(risk.riskySentence)}
                        className="w-full text-left text-gray-700 bg-gray-50 border border-gray-200 rounded p-2 hover:bg-yellow-50 hover:border-yellow-300 transition-colors cursor-pointer"
                        style={{ fontSize: '11px', lineHeight: '1.4' }}
                        title="Click to scroll to this text in document"
                      >
                        {risk.riskySentence}
                      </button>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1" style={{ fontSize: '12px' }}>
                        Suggestion:
                      </h4>
                      <p className="text-gray-700 bg-blue-50 border border-blue-200 rounded p-2" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                        {risk.suggestion}
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1" style={{ fontSize: '12px' }}>
                        Revised Clause:
                      </h4>
                      <p className="text-gray-700 bg-green-50 border border-green-200 rounded p-2" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                        {risk.revisedClause}
                    </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {!isAnalyzing && risks.length === 0 && !error && !allRisksResolved && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600" style={{ fontSize: '13px' }}>
              Click <strong>Run risk analysis</strong> above to scan this
              contract for potential risks. We&apos;ll look for ambiguous
              language, missing safeguards, and one-sided clauses.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
